/**
 * Reusable Error State Component
 * Consistent error UI with retry functionality
 */

import React from 'react';
import { COLORS, SPACING, TYPOGRAPHY, BORDER_RADIUS } from '../../constants/theme';

export interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  fullPage?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  onRetry,
  fullPage = true
}) => {
  const containerStyle: React.CSSProperties = fullPage
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: SPACING['2xl'],
        textAlign: 'center',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: SPACING.xl,
        textAlign: 'center',
      };

  return (
    <div style={containerStyle}>
      {/* Error Icon */}
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: COLORS.danger.light,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: TYPOGRAPHY.fontSize['3xl'],
          marginBottom: SPACING.lg,
        }}
      >
        ⚠️
      </div>

      {/* Title */}
      <h3
        style={{
          fontSize: TYPOGRAPHY.fontSize.xl,
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
          color: COLORS.text.primary,
          marginBottom: SPACING.sm,
        }}
      >
        {title}
      </h3>

      {/* Message */}
      <p
        style={{
          fontSize: TYPOGRAPHY.fontSize.base,
          color: COLORS.text.secondary,
          marginBottom: onRetry ? SPACING.xl : 0,
          maxWidth: '500px',
        }}
      >
        {message}
      </p>

      {/* Retry Button */}
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: `${SPACING.md} ${SPACING.xl}`,
            backgroundColor: COLORS.primary.main,
            color: COLORS.text.inverse,
            border: 'none',
            borderRadius: BORDER_RADIUS.md,
            fontSize: TYPOGRAPHY.fontSize.base,
            fontWeight: TYPOGRAPHY.fontWeight.medium,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = COLORS.primary.hover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = COLORS.primary.main;
          }}
        >
          Try Again
        </button>
      )}
    </div>
  );
};
